import { LogoArea } from 'components/Header/LogoArea';
import dynamic from 'next/dynamic';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import SubMenus from './\bSubMenus';
import DesktopNavList from './DesktopNavList';
import MobileNavList from './MobileNavList';

const HeaderPopupArea = dynamic(() => import('./HeaderPopupArea'), {
  ssr: false,
});

export type Submenus = 'company' | 'product' | null;
export type Products =
  | 'DeepField-SR'
  | 'DeepField-PQO'
  | 'Pulsar-AV1'
  | 'Pulsar-AVIF'
  | 'Pulsar-VMAF';
type Props = {
  isScrolled: boolean;
};

const useMediaQuery = (
  query: string,
  onChange?: (matches: boolean) => void
) => {
  const mqlRef = useRef<MediaQueryList | null>();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia(query);
    mqlRef.current = mql;

    const handler = (e: MediaQueryListEvent) => onChange?.(e.matches);

    if ('addEventListner' in mql) {
      mql.addEventListener('change', handler);
    } else {
      // polyfill for safari old version
      mql.addListener(handler);
    }

    return () => {
      if ('removeEventListner' in mql) {
        mql.removeEventListener('change', handler);
      } else {
        mql.addListener(handler);
      }
    };
  }, [query, onChange]);
};

const useBodyScrollLock = (isOpen: boolean) => {
  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined')
      return;

    if (isOpen) document.body.style.overflowY = 'hidden';
    else document.body.style.overflowY = 'unset';
  }, [isOpen]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // 지원하지 않는 브라우저 대비 try-catch
    try {
      window.history.scrollRestoration = 'manual';
      window.scrollTo({ top: 0 });
    } catch (error) {
      // no-op
    }
  }, []);
};

const Index = ({ isScrolled }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [submenuType, setSubmenuType] = useState<Submenus>(null);
  const layoutRef = useRef();

  const handleMatchMedia = useCallback((matches: boolean) => {
    if (matches) setIsOpen(false);
  }, []);

  // 전체 화면 크기 1048 이하일 경우 상태제어 hooks
  useMediaQuery('(max-width: 1048px)', handleMatchMedia);

  useBodyScrollLock(isOpen);

  return (
    <NewHeaderContainer isScrolled={isScrolled} isOpen={isOpen} ref={layoutRef}>
      {/* logo */}
      <LogoArea isScrolled={isScrolled} isOpen={isOpen} />

      {/* desktop / mobile 메뉴 */}
      <DesktopNavList
        isScrolled={isScrolled}
        setIsOpen={setIsOpen}
        setSubmenuType={setSubmenuType}
      />
      <MobileNavList
        isScrolled={isScrolled}
        setIsOpen={setIsOpen}
        isOpen={isOpen}
      />
      <SubMenus submenuType={submenuType} setSubmenuType={setSubmenuType} />

      <HeaderPopupArea />
    </NewHeaderContainer>
  );
};

export default Index;

type NewHeaderContainerProps = {
  isScrolled: boolean;
  isOpen: boolean;
};

const NewHeaderContainer = styled.header<NewHeaderContainerProps>`
  position: fixed;
  z-index: 9999;
  width: 100%;
  height: 72px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 32px;
  transition: 0.2s all;

  ${({ isScrolled }) =>
    isScrolled &&
    css`
      background: #fff;
      box-shadow: 5px 1px 10px 1px rgba(95, 95, 95, 0.25);
    `};

  ${({ isOpen }) =>
    isOpen &&
    css`
      background: #fff;
    `};

  @media screen and (max-width: 1439px) {
    height: 64px;
  }
  @media screen and (max-width: 1239px) {
    height: 60px;
  }
  @media screen and (max-width: 599px) {
    height: 56px;
    padding: 0 16px;
  }
`;
